import { del, get, post, put } from '@/services/http'
// import { flattenHierarchy } from './flatten'  // with `fetchHierarchyNodes`, retired below
import { toDateFormatKey, toDateFormatWire, toTimeFormatKey } from '@/lib/orgFormats'
import { endpoints } from '@/services/endpoints'

/**
 * The editable asset hierarchy, against the live API.
 *
 * ── One request for the tree, one more per form ──────────────────────────
 * This read the five collections alongside `/assets/hierarchy` for as long as that
 * endpoint carried only `{id, name}` per node. It now carries `short_code` at
 * every level, which was the only thing the collections were still supplying to
 * the tree, so the list is one call and `flatten.js` reshapes it.
 *
 * The fields the tree still does not serve are `is_active`, `asset_class_id` and
 * a machine's `sequence`, and all three are load-bearing: every update here is a
 * full-replace PUT, so a form that never received them would switch the record
 * off, unassign its class and lose its position on save. They are read per
 * record by `fetchNode`, when a form actually opens — one request against a
 * form, rather than five against a page.
 *
 * A plant's `timezone`, `date_format` and `time_format` ARE on the tree, which
 * is why `flatten.js` carries them through: they are required on every plant
 * write, so having them in hand before a form opens is worth the few bytes.
 *
 * ── One shape, two vocabularies ──────────────────────────────────────────
 * The API names its tiers after the backend model: plant › line › machine. This
 * product calls the same tiers site › production line › asset, and every other
 * screen already does. The mapping lives in TIER_RESOURCE below and nowhere
 * else; above this file the app speaks its own vocabulary, and the wire value is
 * never translated.
 *
 * ── There were six tiers. There are four ─────────────────────────────────
 * Region and sub-region are GONE — not deprecated, dropped. Migration
 * `f915c8d4b697` ("drop regions and subregions, plants belong to the account")
 * deleted both tables, moved `plants` onto `account_id`, removed `region` and
 * `subregion` from the `TargetLevel` enum, and the backend serves no router or
 * schema for either any more. A plant's parent is the account itself.
 *
 * This file described the old shape for two days after that landed, and the
 * failure was silent in the way this repo keeps rediscovering: `flatten.js`
 * walked `tree.regions` against a response that now says `tree.plants`, so the
 * whole estate read as empty — no error, no 404, just nothing — across the scope
 * selector, the sensor forms, the anomaly filters and this screen's own
 * drill-down, which asked `/assets/regions` for its first list and got a 404.
 */

/**
 * Ordered. `TIERS[n + 1]` is what may be added beneath a `TIERS[n]`.
 *
 * The account root, then the three real resources. Nothing hangs between the
 * account and a site any more.
 */
export const TIERS = ['enterprise', 'site', 'line', 'asset']

export const childTierOf = (tier) => TIERS[TIERS.indexOf(tier) + 1] ?? null

/**
 * The account is the root, and it has no endpoint — not to create, rename or
 * remove. This id is a client-side sentinel, deliberately not shaped like the
 * UUIDs the server mints, so it can never be mistaken for one and sent back.
 */
export const ROOT_ID = '__account__'

/**
 * Product tier → API resource, the field naming its parent, and the key the
 * collection response wraps its rows in.
 *
 * `parentField` is both the body key on a create and the query parameter that
 * filters the collection — they are the same name on every tier, which is why
 * one entry serves both.
 */
const TIER_RESOURCE = {
  /* A site's parent is the ACCOUNT, which is not a column and not a query
     parameter — the server scopes plants by the token, exactly as it used to
     scope regions. So `parentField` is null here, which `fetchChildren` reads as
     "send no parent filter" and `createNode` as "send no parent key". Sending
     `subregion_id` is now a 400: `tests/assets/test_plant.py` asserts the
     rejection by name. */
  site: { collection: 'plants', detail: 'plant', parentField: null, listKey: 'plants' },
  line: { collection: 'lines', detail: 'line', parentField: 'plant_id', listKey: 'lines' },
  asset: { collection: 'machines', detail: 'machine', parentField: 'line_id', listKey: 'machines' },
}

/**
 * Tiers whose record carries an asset class. The server requires the field on
 * every update of these three — it has no default — so a form that omitted it
 * would not fail loudly, it would 422; and a form that sent null would wipe a
 * real assignment on an unrelated rename.
 */
export const TIERS_WITH_ASSET_CLASS = new Set(['site', 'line', 'asset'])

/** Product tier → the asset-class `entity_type` that may be assigned to it. */
export const ENTITY_TYPE_FOR_TIER = { site: 'plant', line: 'line', asset: 'machine' }

/**
 * Tiers whose record carries a time zone, date order and clock.
 *
 * The SITE and nobody else. `DisplayPreferenceFields` is mixed into `Plant` and
 * into `User`, and into no other schema — so a plant's operating clock is a real
 * field on the hierarchy and there is no organisation-level equivalent to
 * inherit from. All three are REQUIRED on create and on update, with no
 * defaults, which is what a site form omitting them discovers as a 422.
 *
 * This is also where the engineering unit system would belong, if the column
 * existed. It still does not — see BACKEND-GAPS.md item 1.
 */
export const TIERS_WITH_DISPLAY_PREFS = new Set(['site'])

/**
 * Tiers whose record carries an ordering position.
 *
 * The ASSET and nobody else — `sequence` is on `Machine` alone, and it is where
 * a line's machines get their running order. Nullable, but with no default, so
 * the key must be present on every write whether or not a position is set.
 */
export const TIERS_WITH_SEQUENCE = new Set(['asset'])

/**
 * Tiers whose in-service state exists at all.
 *
 * The SITE, the LINE and the ASSET. Taking one of those out of service is a real
 * operational act: a line is down, a machine is retired, a plant is mothballed.
 *
 * Which is now every tier a form can open — the account root has no endpoint, so
 * it never reaches one. The set used to carry a second job: region and
 * sub-region were listed out of it because they had no such column, first as a
 * product decision here and then as a schema decision on the server. Both tiers
 * are gone, so that job is finished.
 *
 * It stays a set rather than collapsing into `true` because it is the record of
 * WHICH schemas declare the field, and that is what `toBody` is asserting when
 * it tests membership. A fourth tier arriving without the column would be a
 * one-line change here instead of a 400 nobody could place.
 *
 * ── It does NOT mean the field is always sent ──────────────────────────
 * `is_active` is update-only: no create body declares it, on any tier. See
 * `toBody`.
 */
export const TIERS_WITH_ACTIVE_TOGGLE = new Set(['site', 'line', 'asset'])

/**
 * Whether this tier has no in-service state to show or set.
 *
 * True only for the account root now, which has no form to show it on. It is
 * kept because `isActive` can still arrive null — `/assets/hierarchy` serves the
 * field at no level — which is why every consumer tests `=== false` rather than
 * falsiness.
 */
export const alwaysActive = (tier) => !TIERS_WITH_ACTIVE_TOGGLE.has(tier)

/** Longest `name` and `short_code` the columns accept. */
export const NAME_MAX_LENGTH = 150
export const CODE_MAX_LENGTH = 50

/**
 * Wire → app. One node shape for all five tiers, so the tree stays a single
 * recursive component rather than five near-identical ones.
 */
const toNode = (tier, parentField) => (row) => ({
  id: row.id,
  tier,
  parentId: parentField ? row[parentField] : ROOT_ID,
  name: row.name,
  code: row.short_code,
  /* `null` is UNKNOWN. The collections all serve it, but `/assets/hierarchy`
     serves it at no level, so a node that came through `flatten.js` carries
     null. Consumers test `=== false`, so an absent value reads as "not out of
     service" rather than as out of service. */
  isActive: row.is_active ?? null,
  assetClassId: row.asset_class_id ?? null,
  /* Site only. Translated into the product's own date vocabulary at this seam,
     the same way the user's copy is — `lib/orgFormats.js` owns both directions
     so the two resources cannot drift apart. */
  timezone: row.timezone ?? null,
  dateFormat: toDateFormatKey(row.date_format),
  timeFormat: toTimeFormatKey(row.time_format),
  /* Asset only. `null` is a real value — unpositioned — and distinct from the
     field being absent, which is a rejection. */
  sequence: row.sequence ?? null,
})

// ── `fetchHierarchyNodes`, RETIRED 2026-10-13 ─────────────────────────────
// The whole-estate read of `GET /assets/hierarchy`. Nothing calls it: places are
// chosen through `HierarchyPicker` and named through `usePlaceNodes`, both over
// the per-level collections this file serves below. Kept commented, per the
// standing recipe; restoring it is uncommenting this, its import, and the
// `hierarchy.tree` endpoint and `LIVE_PATHS` entry.
//
// /**
//  * The whole hierarchy as one flat list, plus the account it belongs to.
//  *
//  * ── One request, not four ─────────────────────────────────────────────────
//  * This used to fetch `/assets/hierarchy` for the account's own name and then the
//  * collections alongside it, purely because the tree did not carry `short_code`.
//  * It does now, at every level, so those reads have nothing left to contribute
//  * and the nested response is flattened here instead.
//  *
//  * Flat rather than nested for the same reason the fixture was: the tree shape is
//  * derived per screen, and every mutation is a single-node write rather than a
//  * recursive patch.
//  *
//  * ── What the tree does NOT carry ──────────────────────────────────────────
//  * `is_active` and `asset_class_id`. Both were coming from the collection reads.
//  * `isActive` is therefore `null` — unknown, not false — and consumers must test
//  * for `=== false` rather than falsiness, or every node reads as out of service.
//  * Anything that needs the real values reads the node itself: see `fetchNode`.
//  */
// export async function fetchHierarchyNodes(signal) {
//   const tree = await get(endpoints.hierarchy.tree, { signal })
//   return {
//     items: flattenHierarchy(tree, ROOT_ID),
//     account: { name: tree?.name ?? '', alias: tree?.alias ?? '' },
//   }
// }

/**
 * One tier's records, under one parent.
 *
 * ── Why this exists beside the tree read ─────────────────────────────────
 * `/assets/hierarchy` returns the WHOLE estate in one nested response, and eight
 * screens want exactly that: the scope selector, the sensor forms and the
 * anomaly filters all need to offer any node in the account. So it stays.
 *
 * What it is wrong for is the Sites & Hierarchy screen, now that the screen
 * drills one level at a time. Fetching every site, line and asset in order to
 * render one list of sites is work proportional to the estate on a screen whose
 * whole point is that it no longer is. These collections are the right shape for
 * it: one request, one generation, filtered by parent.
 *
 * ── And they carry more than the tree does ───────────────────────────────
 * The tree serves `id`, `name`, `short_code`, plus a plant's display
 * preferences. These serve the full record — `is_active`, `asset_class_id`, a
 * machine's `sequence` — which is everything a full-replace PUT has to send
 * back. So a form opened from this list needs no second read of its own.
 *
 * ── `search` is the server's ─────────────────────────────────────────────
 * Passed through rather than filtered in the client, because the client only
 * holds one page. Filtering a page locally would search what happens to be on
 * screen and quietly report nothing on page two.
 *
 * @param {object} input
 * @param {string} input.tier       The tier being LISTED, not its parent's.
 * @param {string|null} input.parentId
 * @param {string} [input.search]
 * @param {number} [input.page]
 * @param {number} [input.pageSize]
 */
export async function fetchChildren({ tier, parentId, search, page = 1, pageSize = 50 }, signal) {
  const { collection, parentField, listKey } = TIER_RESOURCE[tier]

  const params = { page, page_size: pageSize, sort_by: 'short_code', sort_dir: 'asc' }
  /* Sites hang off the account rather than off a row, so they take no parent
     filter — the server scopes plants by the token. `parentField` is null for
     that tier, which is what skips this. */
  if (parentField && parentId) params[parentField] = parentId
  if (search?.trim()) params.search = search.trim()

  const body = await get(endpoints.hierarchy[collection], { params, signal })

  return {
    items: (body?.[listKey] ?? []).map(toNode(tier, parentField)),
    total: body?.total ?? 0,
    page: body?.page ?? page,
    pageSize: body?.page_size ?? pageSize,
    totalPages: body?.total_pages ?? 1,
  }
}

/**
 * How many children a node has.
 *
 * One row of one page, read for its `total` alone — the collections report a
 * count and there is no cheaper way to ask for one. It exists because the
 * removal warning has to be right about the number it states, and with one
 * generation on screen at a time the page cannot count a row's children itself.
 *
 * Asking per row would be one request per row. So this is asked once, when a
 * form opens, about the one record that form can delete.
 */
export async function fetchChildCount({ tier, id }, signal) {
  const childTier = childTierOf(tier)
  if (!childTier) return 0
  const { total } = await fetchChildren({ tier: childTier, parentId: id, pageSize: 1 }, signal)
  return total
}

/**
 * A node and every ancestor above it, outermost first.
 *
 * Only for a COLD arrival — someone opening `?tier=line&node=…` from a link or a
 * reload. Drilling in from the screen already holds each node it passed through,
 * so nothing is re-read on the way down.
 *
 * Up to three requests, serially, because each record names only its immediate
 * parent and the next call cannot be formed until the previous answers. That is
 * the cost of the tree read no longer being made, and it is paid once per cold
 * link rather than on every visit.
 *
 * The account root is not fetched: it has no endpoint. The caller prepends it.
 */
export async function fetchAncestors({ tier, id }, signal) {
  const path = []
  let target = { tier, id }

  while (target) {
    const node = await fetchNode(target, signal)
    path.unshift(node)
    const parentTier = TIERS[TIERS.indexOf(node.tier) - 1]
    target =
      node.parentId && node.parentId !== ROOT_ID && parentTier && parentTier !== 'enterprise'
        ? { tier: parentTier, id: node.parentId }
        : null
  }

  return path
}

/**
 * One node, read on its own.
 *
 * The tree endpoint carries `id`, `name` and `short_code` and nothing else, so an
 * edit form built from it would prefill `is_active` and `asset_class_id` as blanks
 * — and every update in this API is a FULL REPLACE, so saving would then switch
 * the record off and unassign its asset class without anyone asking for either.
 *
 * So the editor reads the record it is about to overwrite. One request, opened
 * only when a form opens, against endpoints that exist for all three real tiers.
 *
 * The Sites & Hierarchy editor no longer needs it — its list IS the collections,
 * which serve the whole record — but `fetchAncestors` walks through it, and the
 * user editor reads a stored scope's node with it.
 */
export async function fetchNode({ tier, id }, signal) {
  const { detail, parentField } = TIER_RESOURCE[tier]
  const row = await get(endpoints.hierarchy[detail](id), { signal })
  return toNode(tier, parentField)(row)
}

/**
 * App → wire.
 *
 * `asset_class_id` is included only for the tiers that have the column, and is
 * sent even when null — the update schemas require the key to be present, and
 * omitting it is a rejection rather than a no-op.
 *
 * ── `is_active` is UPDATE-ONLY ──────────────────────────────────────────
 * No CREATE body takes it. `PlantCreate`, `LineCreate` and `MachineCreate` have
 * no `is_active`; a thing is made in service, and taking it out of service is a
 * later act. Only the `*Update` schemas carry the field.
 *
 * Sending it on a create is a 400, not a silently ignored extra: every request
 * body is validated with `extra="forbid"` (`app/core/http/strict.py`), so any
 * key a schema does not declare is refused by name.
 *
 * @param {'create'|'update'} mode
 */
function toBody(tier, mode, { name, code, isActive, assetClassId, timezone, dateFormat, timeFormat, sequence }) {
  return {
    name: name.trim(),
    short_code: code.trim(),
    ...(mode === 'update' && TIERS_WITH_ACTIVE_TOGGLE.has(tier) ? { is_active: isActive } : {}),
    ...(TIERS_WITH_ASSET_CLASS.has(tier) ? { asset_class_id: assetClassId || null } : {}),
    /* Required on a plant, all three, on create and update alike. Sent for that
       tier and no other — a line and a machine have no column for them and would
       reject the keys outright. */
    ...(TIERS_WITH_DISPLAY_PREFS.has(tier)
      ? {
          timezone,
          date_format: toDateFormatWire(dateFormat),
          time_format: timeFormat,
        }
      : {}),
    /* Required on a machine, and nullable. `?? null` rather than `|| null`,
       because 0 is a legitimate position and falsy. */
    ...(TIERS_WITH_SEQUENCE.has(tier) ? { sequence: sequence ?? null } : {}),
  }
}

/** @param {{parentTier: string, parentId: string, values: object}} input */
export async function createNode({ parentTier, parentId, values }) {
  const tier = childTierOf(parentTier)
  const { collection, parentField } = TIER_RESOURCE[tier]
  const row = await post(endpoints.hierarchy[collection], {
    ...(parentField ? { [parentField]: parentId } : {}),
    ...toBody(tier, 'create', values),
  })
  return toNode(tier, parentField)(row)
}

/**
 * A full replace, not a patch. The API has no partial-update semantics
 * anywhere, so the caller sends every editable field every time — which is also
 * why the form is populated from the record rather than starting empty.
 */
export async function updateNode({ tier, id, values }) {
  const { detail, parentField } = TIER_RESOURCE[tier]
  const row = await put(endpoints.hierarchy[detail](id), toBody(tier, 'update', values))
  return toNode(tier, parentField)(row)
}

/**
 * Soft delete, and it does NOT cascade or refuse.
 *
 * The server marks this one row deleted and stops there. Its children keep
 * `deleted_at` null and — because the collection queries filter the child's own
 * deleted flag but not its parent's — remain listed, now orphaned under a parent
 * that no longer exists. They vanish from the TREE, which is built by walking
 * down from live parents, while still holding their short codes against the
 * sibling-uniqueness check.
 *
 * The screen used to refuse this. It does not any more, by instruction — see the
 * page's own header. It warns twice instead, in the panel and again in the
 * dialog, which is the most a client can do about a server-side gap. See
 * BACKEND-GAPS.md item 2.
 */
export async function deleteNode({ tier, id }) {
  const { detail } = TIER_RESOURCE[tier]
  await del(endpoints.hierarchy[detail](id))
  return { id, deleted: true }
}
